/**
 * 能力检测。
 *
 * 规则：**一律 feature detection，绝不用 UA 白名单**。UA 只用来决定给用户看哪段文案
 * （见 platform.ts）。
 *
 * 有一个例外要说清楚：Declarative Web Push 在页面里**没有可检测的特征**。所以框架不假装
 * 能检测它——服务端总是发 declarative 兼容的负载，支持的平台自动兜底，不支持的平台走
 * Service Worker 的 push 处理器。两边都不需要判断。
 */
import { currentPlatform } from './platform.js';
import { isStandalone } from './standalone.js';
import { fail, ok } from './result.js';
function has(obj, prop) {
    return typeof obj === 'object' && obj !== null && prop in obj;
}
export function detectCapabilities() {
    const hasWindow = typeof window !== 'undefined';
    const nav = typeof navigator === 'undefined' ? undefined : navigator;
    const secureContext = typeof isSecureContext === 'boolean' ? isSecureContext : hasWindow && location.protocol === 'https:';
    const swSupported = !!nav && 'serviceWorker' in nav;
    return {
        secureContext,
        serviceWorker: swSupported,
        notification: hasWindow && 'Notification' in window,
        push: hasWindow && 'PushManager' in window,
        badge: !!nav && 'setAppBadge' in nav,
        wakeLock: !!nav && 'wakeLock' in nav,
        share: !!nav && typeof nav.share === 'function',
        shareFiles: !!nav && typeof nav.canShare === 'function',
        backgroundSync: hasWindow && 'ServiceWorkerRegistration' in window && has(ServiceWorkerRegistration.prototype, 'sync'),
        periodicSync: hasWindow && 'ServiceWorkerRegistration' in window && has(ServiceWorkerRegistration.prototype, 'periodicSync'),
        viewTransitions: typeof document !== 'undefined' && typeof document.startViewTransition === 'function',
        persistentStorage: !!nav && !!nav.storage && typeof nav.storage.persist === 'function',
        installPrompt: hasWindow && 'onbeforeinstallprompt' in window,
        standalone: isStandalone(),
    };
}
/**
 * 把「为什么不能用通知/推送」翻译成对用户有意义的原因。
 *
 * iOS 上 `Notification` 在 Safari 标签页里根本不存在，只有从主屏图标打开的 Web App 里才有。
 * 直接报 'unsupported' 会让用户以为是浏览器不行，实际上他只要加到主屏就能用——
 * 这两句提示的差别，就是用户装不装得成的差别。
 */
export function explainNotificationSupport(caps = detectCapabilities()) {
    if (!caps.secureContext)
        return fail('insecure-context');
    if (caps.notification)
        return ok();
    const platform = currentPlatform();
    if (platform.isIOS && !caps.standalone)
        return fail('not-installed');
    return fail('unsupported');
}
export function explainPushSupport(caps = detectCapabilities()) {
    const base = explainNotificationSupport(caps);
    if (!base.ok)
        return base;
    if (!caps.serviceWorker)
        return fail('unsupported', 'no serviceWorker');
    if (!caps.push)
        return fail('unsupported', 'no PushManager');
    return ok();
}
/**
 * 角标能不能用。
 *
 * iOS 上角标虽然有 API，但**受通知权限控制**：没授权通知，setAppBadge 不报错也不显示。
 * 所以这里把"通知没授权"也算作不可用，否则调用方会以为设成功了。
 */
export function explainBadgeSupport(caps = detectCapabilities(), notificationPermission = readNotificationPermission()) {
    if (!caps.badge) {
        const platform = currentPlatform();
        if (platform.isIOS && !caps.standalone)
            return fail('not-installed');
        return fail('unsupported');
    }
    if (notificationPermission === 'denied')
        return fail('permission-denied');
    if (notificationPermission === 'default')
        return fail('permission-default');
    return ok();
}
export function readNotificationPermission() {
    if (typeof window === 'undefined' || !('Notification' in window))
        return 'unsupported';
    try {
        return Notification.permission;
    }
    catch {
        return 'unsupported';
    }
}
//# sourceMappingURL=capability.js.map